import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { CaseStudy } from '../types';
interface CaseStudyDetailProps {
  study: CaseStudy | null;
  isOpen: boolean;
  onClose: () => void;
}
export function CaseStudyDetail({
  study,
  isOpen,
  onClose
}: CaseStudyDetailProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleEscape);
      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);
  if (!study) return null;
  const Section = ({
    title,
    content
  }: {
    title: string;
    content: string;
  }) => <div className="mb-16" data-id="element-56">
      <h3 className="text-xl font-semibold text-primary mb-4 tracking-tight" data-id="element-57">
        {title}
      </h3>
      <p className="text-secondary leading-relaxed text-lg" data-id="element-58">{content}</p>
    </div>;
  return <AnimatePresence data-id="element-59">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-dark overflow-y-auto" data-id="element-60">
          <div className="min-h-screen" data-id="element-61">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-dark/80 backdrop-blur-md border-b border-border" data-id="element-62">
              <div className="max-w-4xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between" data-id="element-63">
                <span className="text-xs text-secondary font-medium tracking-wider uppercase" data-id="element-64">
                  {study.domain}
                </span>
                <button onClick={onClose} className="text-secondary hover:text-primary transition-colors p-2 -mr-2 bg-dark-surface rounded-full border border-border" aria-label="Close" data-id="element-65">
                  <X className="w-4 h-4" data-id="element-66" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24" data-id="element-67">
              {/* Hero */}
              <div className="mb-20" data-id="element-68">
                <div className="mb-6 flex items-center gap-2 text-sm text-secondary font-medium" data-id="element-69">
                  <span className="text-primary" data-id="element-70">{study.company}</span>
                  <span data-id="element-71">·</span>
                  <span data-id="element-72">{study.timeframe}</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight tracking-tight mb-12" data-id="element-73">
                  {study.title}
                </h1>

                <div className="glass-card p-8 md:p-12 rounded-2xl mb-12" data-id="element-74">
                  <div className="text-5xl md:text-7xl font-bold text-gold tabular-nums tracking-tight mb-4" data-id="element-75">
                    {study.heroMetric}
                  </div>
                  <p className="text-secondary text-lg md:text-xl font-medium" data-id="element-76">
                    {study.heroMetricContext}
                  </p>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-77">
                  {study.keyMetrics.map((metric, index) => <div key={index} className="glass-card p-6 rounded-xl" data-id="element-78">
                      <div className="text-2xl font-bold text-primary tabular-nums tracking-tight mb-1" data-id="element-79">
                        {metric.value}
                      </div>
                      <div className="text-xs text-secondary font-medium" data-id="element-80">
                        {metric.label}
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Sections */}
              <div className="max-w-prose" data-id="element-81">
                <Section title="Context" content={study.context} data-id="element-82" />
                <Section title="Problem" content={study.problem} data-id="element-83" />
                <Section title="Constraints" content={study.constraints} data-id="element-84" />
                <Section title="Decision Framework" content={study.decisionFramework} data-id="element-85" />
                <Section title="Execution" content={study.execution} data-id="element-86" />
                <Section title="Metrics & Impact" content={study.impact} data-id="element-87" />
                <Section title="Lessons & What I'd Do Differently" content={study.lessons} data-id="element-88" />
              </div>

              {/* Close */}
              <div className="mt-24 pt-8 border-t border-border" data-id="element-89">
                <button onClick={onClose} className="text-primary hover:text-gold transition-colors text-sm font-medium flex items-center gap-2" data-id="element-90">
                  ← Back to all work
                </button>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}